import React, { useRef, useState } from 'react'
import { Column } from 'primereact/column'
import { useSelector } from 'react-redux'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { PrimeIcons } from 'primereact/api'
import { Menu } from 'primereact/menu'

import { Table } from '../../uikit/Table'
import { NfcTag, useNfcTagDeleteMutation } from '../../redux/api/web-api'
import convertHexToBigInt from '../../../lib/convertHexToBigInt'
import { RootState } from '../../../admin/redux/store'
import { RfidTagModal } from '../rfid-tag-modal'
import { useRFIDTagsQuery } from '../../hooks/useFetchEndpoints'
import { isAdminDomain } from '../../../lib/currentDomain'
import { Input } from '../../uikit/Input'
import { RfidTagPoolsModal } from '../rfid-tag-pools-modal'
import { RfidTagChargepointsModal } from '../rfid-tag-chargepoints-modal/rfid-tag-chargepoints-modal'
import useText from '../../hooks/useText'
import { useToast } from '../../uikit/ToastProvider'

export const RFIDTagsWidget = () => {
    const text = useText()
    const { partnerId, selectedCompanyId } = useSelector((state: RootState) => state.user)

    const { data: rfidTags = [], isLoading: isLoadingRfidTags } = useRFIDTagsQuery(partnerId, selectedCompanyId)
    const [deleteTag] = useNfcTagDeleteMutation()

    const [globalFilter, setGlobalFilter] = useState('')

    const menuRef = useRef<Menu>(null)
    const toast = useToast()

    const [rfidTag, setRfidTag] = useState<NfcTag | undefined>(undefined)
    const [rfidTagModalVisible, setRfidTagModalVisible] = useState(false)
    const [poolsModalIsVisible, setPoolsModalIsVisible] = useState(false)
    const [chargepointsModalIsVisible, setChargepointsModalIsVisible] = useState(false)

    const editAdmin = (rfidTag: NfcTag) => {
        setRfidTag(rfidTag)
        setRfidTagModalVisible(true)
    }

    const closeRfidTagModal = () => {
        setRfidTagModalVisible(false)
        setRfidTag(undefined)
    }

    const confirmDelete = (rfidTag: NfcTag) => {
        confirmDialog({
            header: text('lblDeleteRFID'),
            message: text('msgDoYouWantToDeleteRFID', { id: rfidTag.id }),
            icon: 'pi pi-info-circle',
            position: 'bottom',
            accept: async () => {
                deleteTag({
                    nfcTagId: rfidTag.id!,
                })
                    .unwrap()
                    .then(() => {
                        toast.notifySuccess(text('msgRFIDDeleted'))
                    })
                    .catch(() => {
                        toast.notifyError(text('msgRFIDNotDeleted'))
                    })
            },
        })
    }
    return (
        <>
            <div className="flex align-items-center justify-content-end mb-4">
                <Button icon="pi pi-plus" onClick={() => setRfidTagModalVisible(true)} size="small" className="mr-2" />
                <Input
                    id="rfid-search-input"
                    type="search"
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder={text('msgSearch')}
                    icon={PrimeIcons.SEARCH}
                />
            </div>

            <Menu
                model={[
                    {
                        label: text('lblRFIDAccessToPools'),
                        command: () => setPoolsModalIsVisible(true),
                        disabled: !rfidTag?.companyId,
                    },
                    {
                        label: text('lblRFIDAccessToChargers'),
                        command: () => setChargepointsModalIsVisible(true),
                        disabled: !rfidTag?.companyId,
                    },
                ]}
                ref={menuRef}
                popup
            />

            <Table
                value={rfidTags}
                globalFilter={globalFilter}
                globalFilterFields={['id', 'companyName', 'fullName', 'externalId', 'comment']}
                removableSort
                loading={isLoadingRfidTags}
                dataKey="id"
                paginator
                rows={10}
                emptyMessage={text('msgNoRfidTagsFound')}
                data-testid="rfid-tags"
            >
                <Column field="id" header={text('lblId')} sortable></Column>
                <Column
                    body={(rowData: NfcTag) => convertHexToBigInt(rowData.id!)}
                    header={text('lblRfidDex')}
                ></Column>
                <Column
                    body={(rowData: NfcTag) => convertHexToBigInt(rowData.id!, true)}
                    header={text('lblRfidReverse')}
                ></Column>
                <Column
                    field="companyName"
                    header={text('lblOrganisationNameField')}
                    sortable
                    hidden={isAdminDomain}
                ></Column>
                <Column field="fullName" header={text('lblUserOrOwner')} sortable></Column>
                <Column field="externalId" header={text('lblExternalId')} sortable></Column>
                <Column field="comment" header={text('lblComment')} sortable></Column>

                <Column
                    body={(rowData: NfcTag) => {
                        return (
                            <div className="flex gap-3">
                                <Button
                                    size="small"
                                    icon="pi pi-pencil"
                                    rounded
                                    outlined
                                    severity="info"
                                    onClick={() => editAdmin(rowData)}
                                />
                                <Button
                                    size="small"
                                    icon="pi pi-cog"
                                    rounded
                                    outlined
                                    onClick={(event) => {
                                        setRfidTag(rowData)
                                        menuRef.current?.toggle(event)
                                    }}
                                    disabled={!rowData.companyId}
                                    tooltip={rowData?.companyId ? undefined : text('lblRFIDSettingsRestrictionTooltip')}
                                    tooltipOptions={{ position: 'left', showOnDisabled: true }}
                                />
                                <Button
                                    size="small"
                                    icon="pi pi-trash"
                                    rounded
                                    outlined
                                    severity="danger"
                                    onClick={() => confirmDelete(rowData)}
                                />
                            </div>
                        )
                    }}
                ></Column>
            </Table>

            {rfidTagModalVisible ? <RfidTagModal rfidTag={rfidTag} onClose={closeRfidTagModal} /> : null}

            <RfidTagPoolsModal
                visible={poolsModalIsVisible}
                onHide={() => setPoolsModalIsVisible(false)}
                rfidTag={rfidTag}
            />

            <RfidTagChargepointsModal
                visible={chargepointsModalIsVisible}
                onHide={() => setChargepointsModalIsVisible(false)}
                rfidTag={rfidTag}
            />
        </>
    )
}
